import React, { useEffect } from 'react'
import TagManager from 'react-gtm-module'

import { useDashboardPermissions, usePlatformFeatureFlags } from '@components'
import { Button, Toasts } from '@fourthwall/components'
import { useShopStage } from '@hooks'

import { useGetCampaignQuery } from '../../../../../hooks/useGetCampaignQuery'
import type { Incentive, Recommendation } from '../../../Recommendations.types'
import { useRecommendationsDismiss } from '../../../hooks/useRecommendationsDismiss'
import { AppsIncentive } from '../components/AppsIncentive'
import { Campaign } from '../components/Campaign'
import { EnrollBanner } from '../components/Campaign/EnrollBanner'
import { DiscordIncentive } from '../components/DiscordIncentive'
import { DoubleReferralBonusIncentive } from '../components/DoubleReferralBonusIncentive'
import { MakeYourFirstSalesIncentive } from '../components/MakeYourFirstSalesIncentive'
import { MobileAppIncentive } from '../components/MobileAppIncentive'
import { ProductsIncentive } from '../components/ProductsIncentive'
import { PromotionIncentive } from '../components/PromotionIncentive'
import { SamplesIncentive } from '../components/SamplesIncentive'
import { useEnrollCampaignMutation } from '../queries/useEnrollCampaignMutation'

function nonNullable<T>(value: T): value is NonNullable<T> {
  return Boolean(value)
}

export const useCampaignRecommendation = () => {
  const { isLoading: isLoadingShopStage } = useShopStage()
  const { hasFeatureFlag } = usePlatformFeatureFlags()
  const { hasPermission } = useDashboardPermissions()

  const { recommendationsDismiss, createOnDismiss } = useRecommendationsDismiss()

  const { campaignQuery } = useGetCampaignQuery(undefined, {
    enabled: hasPermission('home.campaign'),
  })
  const { enrollCampaignMutation } = useEnrollCampaignMutation()

  useEffect(() => {
    if (!campaignQuery.data?.onboardingCampaignId) return

    TagManager.dataLayer({
      dataLayer: {
        onboardingCampaignId: campaignQuery.data?.onboardingCampaignId,
      },
    })
  }, [campaignQuery.data?.onboardingCampaignId])

  const campaign = campaignQuery.data

  const visible = recommendationsDismiss.campaign.visible && campaign

  const getIncentive = (incentive: Incentive) => {
    switch (incentive.type) {
      case 'VERIFY_DISCORD':
        return <DiscordIncentive incentive={incentive} />
      case 'CREATE_N_OR_MORE_PRODUCTS':
        return <ProductsIncentive incentive={incentive} />
      case 'INSTALL_MOBILE_APP':
        return <MobileAppIncentive incentive={incentive} />
      case 'INSTALL_N_OR_MORE_APPS':
        return <AppsIncentive incentive={incentive} />
      case 'CREATE_PROMOTION':
        return <PromotionIncentive incentive={incentive} />
      case 'ORDER_SAMPLES':
        return <SamplesIncentive incentive={incentive} />
      case 'MAKE_FIRST_SALES':
        return <MakeYourFirstSalesIncentive incentive={incentive} />
      case 'DOUBLE_SIGNUP_REFERRAL_BONUS':
        return <DoubleReferralBonusIncentive incentive={incentive} />
    }
  }

  const getSuggestion = () => {
    if (!visible) return null

    if (!campaign.isEnrolled) {
      return (
        <EnrollBanner.Root>
          <EnrollBanner.Content>
            <EnrollBanner.Title>{campaign?.displayInfo.title}</EnrollBanner.Title>
            <EnrollBanner.Description>
              {campaign?.displayInfo.description!}
            </EnrollBanner.Description>
          </EnrollBanner.Content>
          <EnrollBanner.Actions>
            {campaign?.displayInfo.primaryButton?.title && (
              <Button
                label={campaign?.displayInfo.primaryButton?.title!}
                appearance="semi-transparent"
                size="small"
                onClick={async () => {
                  await enrollCampaignMutation.mutateAsync(
                    [{ campaignId: campaign.onboardingCampaignId }],
                    {
                      onSuccess: () => {
                        campaignQuery.refetch()
                        Toasts.notify('You have successfully enrolled in the campaign.', {
                          type: 'success',
                        })
                      },
                    },
                  )
                }}
                disabled={enrollCampaignMutation.isLoading}
              />
            )}
            {campaign?.displayInfo.secondaryButton?.title && (
              <Button
                label={campaign?.displayInfo.secondaryButton?.title!}
                href={campaign?.displayInfo.secondaryButton?.uri!}
                target="_blank"
                appearance="semi-transparent"
                size="small"
              />
            )}
          </EnrollBanner.Actions>
        </EnrollBanner.Root>
      )
    }

    if (hasFeatureFlag('dashboard_onboarding_campagin_june_2026')) {
      return (
        <Campaign
          title={campaign.displayInfo.title}
          description={campaign.displayInfo.description}
          bonusSummary={campaign.bonusSummary}
          incentives={campaign.campaignIncentives.map(getIncentive).filter(nonNullable)}
          campaignIncentives={campaign.campaignIncentives}
          onDismiss={createOnDismiss(recommendationsDismiss.campaign.dismiss)}
        />
      )
    }

    return null
  }

  const suggestion = getSuggestion()

  return {
    campaignRecommendation: {
      id: 'campaign',
      enabled: Boolean(suggestion),
      isLoading: isLoadingShopStage || campaignQuery.isInitialLoading,
      suggestion,
    } satisfies Recommendation,
  }
}
